import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  deleteDoc,
  getDocs,
  type QuerySnapshot,
  type DocumentData
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth-context';
import { PROJECTS, type Checklist, type Project } from '../types';
import CreateChecklistModal from '../components/CreateChecklistModal';

export default function ChecklistsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState<Project | 'ALL'>('ALL');
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'checklists'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap: QuerySnapshot<DocumentData>) => {
      const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Checklist);
      setChecklists(rows);
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered =
    projectFilter === 'ALL'
      ? checklists
      : checklists.filter((c) => c.project === projectFilter);

  return (
    <div className="page page--checklists">
      <div className="page__header">
        <div>
          <h1 className="page__title">Checklists</h1>
          <p className="page__subtitle">
            {profile?.role === 'admin'
              ? 'Manage QC checklists across every project.'
              : 'Walk through assigned checklists. Mark each item pass or fail.'}
          </p>
        </div>

        {profile?.role === 'admin' && (
          <button className="btn btn--primary" onClick={() => setCreateOpen(true)}>
            + New checklist
          </button>
        )}
      </div>

      <CreateChecklistModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => navigate(`/checklists/${id}`)}
      />

      <div className="filter-bar">
        <button
          className={`filter-bar__chip ${projectFilter === 'ALL' ? 'is-active' : ''}`}
          onClick={() => setProjectFilter('ALL')}
        >
          All projects
        </button>
        {PROJECTS.map((p) => (
          <button
            key={p.value}
            className={`filter-bar__chip ${projectFilter === p.value ? 'is-active' : ''}`}
            onClick={() => setProjectFilter(p.value)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="empty">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          {projectFilter === 'ALL'
            ? 'No checklists yet.'
            : `No checklists for ${PROJECTS.find((p) => p.value === projectFilter)?.label}.`}
        </div>
      ) : (
        <ul className="checklist-list">
          {filtered.map((c) => (
            <li key={c.id} className="checklist-card">
              <Link to={`/checklists/${c.id}`} className="checklist-card__link">
                <div className="checklist-card__head">
                  <h2 className="checklist-card__title">{c.title}</h2>
                  <span className={`tag tag--${c.project.toLowerCase()}`}>{c.project}</span>
                </div>

                {c.description && (
                  <p className="checklist-card__desc">{c.description}</p>
                )}

                <div className="checklist-card__meta">
                  <span className="meta">
                    <span className="meta__dot meta__dot--pass" />
                    {c.passCount} pass
                  </span>
                  <span className="meta">
                    <span className="meta__dot meta__dot--fail" />
                    {c.failCount} fail
                  </span>
                  <span className="meta">
                    <span className="meta__dot meta__dot--pending" />
                    {c.pendingCount} pending
                  </span>
                  <span className="meta meta--muted">
                    by {c.createdByName}
                  </span>
                </div>

                <div className="checklist-card__progress">
                  <div
                    className="checklist-card__progress-fill"
                    style={{
                      width: `${
                        c.itemCount > 0
                          ? ((c.passCount + c.failCount) / c.itemCount) * 100
                          : 0
                      }%`
                    }}
                  />
                </div>
              </Link>
              {profile?.role === 'admin' && (
                <button
                  className="checklist-card__delete"
                  title="Delete checklist (and all its items)"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!confirm(`Delete checklist "${c.title}" and all its items? This can't be undone.`)) return;
                    // delete all items first
                    const itemsSnap = await getDocs(collection(db, 'checklists', c.id, 'items'));
                    await Promise.all(itemsSnap.docs.map((d) => deleteDoc(d.ref)));
                    await deleteDoc(doc(db, 'checklists', c.id));
                  }}
                >
                  ✕
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
